/**
 * Marketplace API endpoints (dashboard admin side)
 */
import { api } from './baseApi';
import type {
  MarketplaceCategory,
  MarketplaceSubCategory,
  MarketplaceProduct,
  MarketplaceProductListItem,
  MarketplaceOrder,
  ProductImage,
  MarketplaceOrderStatus,
  MarketplacePaymentListItem,
  MarketplaceOrderStats,
  MarketplacePaymentStats,
} from '../../types/marketplace';

interface PaginatedResponse<T> {
  success: boolean;
  count: number;
  next: string | null;
  previous: string | null;
  total_pages: number;
  current_page: number;
  results: T[];
}

interface DataResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

export const marketplaceApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ===== Categories =====
    getMarketplaceCategories: builder.query<
      PaginatedResponse<MarketplaceCategory>,
      { search?: string; is_active?: boolean; page?: number; page_size?: number }
    >({
      query: (params) => ({ url: '/dashboard/marketplace/categories/', params }),
      providesTags: ['MarketplaceCategories'],
    }),
    getMarketplaceCategory: builder.query<DataResponse<MarketplaceCategory>, number>({
      query: (id) => `/dashboard/marketplace/categories/${id}/`,
      providesTags: (_r, _e, id) => [{ type: 'MarketplaceCategory', id }],
    }),
    createMarketplaceCategory: builder.mutation<DataResponse<MarketplaceCategory>, FormData>({
      query: (body) => ({ url: '/dashboard/marketplace/categories/', method: 'POST', body }),
      invalidatesTags: ['MarketplaceCategories'],
    }),
    updateMarketplaceCategory: builder.mutation<DataResponse<MarketplaceCategory>, { id: number; data: FormData }>({
      query: ({ id, data }) => ({ url: `/dashboard/marketplace/categories/${id}/`, method: 'PATCH', body: data }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'MarketplaceCategory', id }, 'MarketplaceCategories'],
    }),
    deleteMarketplaceCategory: builder.mutation<void, number>({
      query: (id) => ({ url: `/dashboard/marketplace/categories/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['MarketplaceCategories'],
    }),

    // ===== SubCategories =====
    getMarketplaceSubCategories: builder.query<
      PaginatedResponse<MarketplaceSubCategory>,
      { category?: number; search?: string; is_active?: boolean; page?: number; page_size?: number }
    >({
      query: (params) => ({ url: '/dashboard/marketplace/subcategories/', params }),
      providesTags: ['MarketplaceSubCategories'],
    }),
    getMarketplaceSubCategory: builder.query<DataResponse<MarketplaceSubCategory>, number>({
      query: (id) => `/dashboard/marketplace/subcategories/${id}/`,
      providesTags: (_r, _e, id) => [{ type: 'MarketplaceSubCategory', id }],
    }),
    createMarketplaceSubCategory: builder.mutation<DataResponse<MarketplaceSubCategory>, FormData>({
      query: (body) => ({ url: '/dashboard/marketplace/subcategories/', method: 'POST', body }),
      invalidatesTags: ['MarketplaceSubCategories'],
    }),
    updateMarketplaceSubCategory: builder.mutation<DataResponse<MarketplaceSubCategory>, { id: number; data: FormData }>({
      query: ({ id, data }) => ({ url: `/dashboard/marketplace/subcategories/${id}/`, method: 'PATCH', body: data }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'MarketplaceSubCategory', id }, 'MarketplaceSubCategories'],
    }),
    deleteMarketplaceSubCategory: builder.mutation<void, number>({
      query: (id) => ({ url: `/dashboard/marketplace/subcategories/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['MarketplaceSubCategories'],
    }),

    // ===== Products =====
    getMarketplaceProducts: builder.query<
      PaginatedResponse<MarketplaceProductListItem>,
      {
        subcategory?: number;
        category?: number;
        pricing_type?: string;
        is_active?: boolean;
        search?: string;
        page?: number;
        page_size?: number;
      }
    >({
      query: (params) => ({ url: '/dashboard/marketplace/products/', params }),
      providesTags: ['MarketplaceProducts'],
    }),
    getMarketplaceProduct: builder.query<DataResponse<MarketplaceProduct>, number>({
      query: (id) => `/dashboard/marketplace/products/${id}/`,
      providesTags: (_r, _e, id) => [{ type: 'MarketplaceProduct', id }],
    }),
    // Body is a JSON object (supports nested specifications). For main_image
    // uploads, send FormData with a JSON-encoded `specifications` field instead.
    createMarketplaceProduct: builder.mutation<DataResponse<MarketplaceProduct>, FormData | Record<string, unknown>>({
      query: (body) => ({ url: '/dashboard/marketplace/products/', method: 'POST', body }),
      invalidatesTags: ['MarketplaceProducts'],
    }),
    updateMarketplaceProduct: builder.mutation<
      DataResponse<MarketplaceProduct>,
      { id: number; data: FormData | Record<string, unknown> }
    >({
      query: ({ id, data }) => ({ url: `/dashboard/marketplace/products/${id}/`, method: 'PATCH', body: data }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'MarketplaceProduct', id }, 'MarketplaceProducts'],
    }),
    deleteMarketplaceProduct: builder.mutation<void, number>({
      query: (id) => ({ url: `/dashboard/marketplace/products/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['MarketplaceProducts'],
    }),

    // ===== Product images =====
    uploadProductImage: builder.mutation<DataResponse<ProductImage>, { productId: number; data: FormData }>({
      query: ({ productId, data }) => ({
        url: `/dashboard/marketplace/products/${productId}/images/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_r, _e, { productId }) => [{ type: 'MarketplaceProduct', id: productId }],
    }),
    deleteProductImage: builder.mutation<void, { productId: number; imageId: number }>({
      query: ({ productId, imageId }) => ({
        url: `/dashboard/marketplace/products/${productId}/images/${imageId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { productId }) => [{ type: 'MarketplaceProduct', id: productId }],
    }),

    // ===== Orders =====
    getMarketplaceOrders: builder.query<
      PaginatedResponse<MarketplaceOrder>,
      { status?: string; user_id?: number; search?: string; page?: number; page_size?: number }
    >({
      query: (params) => ({ url: '/dashboard/marketplace/orders/', params }),
      providesTags: ['MarketplaceOrders'],
    }),
    getMarketplaceOrder: builder.query<DataResponse<MarketplaceOrder>, number>({
      query: (id) => `/dashboard/marketplace/orders/${id}/`,
      providesTags: (_r, _e, id) => [{ type: 'MarketplaceOrder', id }],
    }),
    updateMarketplaceOrderStatus: builder.mutation<
      DataResponse<MarketplaceOrder>,
      { id: number; status: MarketplaceOrderStatus; admin_notes?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/dashboard/marketplace/orders/${id}/status/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'MarketplaceOrder', id },
        'MarketplaceOrders',
        'MarketplacePayments',
      ],
    }),
    getMarketplacePayments: builder.query<
      PaginatedResponse<MarketplacePaymentListItem>,
      {
        status?: string;
        payment_method?: string;
        order_id?: number;
        user_id?: number;
        date_from?: string;
        date_to?: string;
        search?: string;
        page?: number;
        page_size?: number;
      }
    >({
      query: (params) => ({ url: '/dashboard/marketplace/payments/', params }),
      providesTags: ['MarketplacePayments'],
    }),
    getMarketplaceOrderStats: builder.query<DataResponse<MarketplaceOrderStats>, void>({
      query: () => '/dashboard/marketplace/orders/statistics/',
      providesTags: ['MarketplaceOrders'],
    }),
    getMarketplacePaymentStats: builder.query<DataResponse<MarketplacePaymentStats>, void>({
      query: () => '/dashboard/marketplace/payments/statistics/',
      providesTags: ['MarketplacePayments'],
    }),
    updateMarketplacePaymentStatus: builder.mutation<
      DataResponse<MarketplacePaymentListItem>,
      { id: number; status: string; admin_notes?: string; failure_reason?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/dashboard/marketplace/payments/${id}/status/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['MarketplacePayments', 'MarketplaceOrders'],
    }),
  }),
});

export const {
  useGetMarketplaceCategoriesQuery,
  useGetMarketplaceCategoryQuery,
  useCreateMarketplaceCategoryMutation,
  useUpdateMarketplaceCategoryMutation,
  useDeleteMarketplaceCategoryMutation,
  useGetMarketplaceSubCategoriesQuery,
  useGetMarketplaceSubCategoryQuery,
  useCreateMarketplaceSubCategoryMutation,
  useUpdateMarketplaceSubCategoryMutation,
  useDeleteMarketplaceSubCategoryMutation,
  useGetMarketplaceProductsQuery,
  useGetMarketplaceProductQuery,
  useCreateMarketplaceProductMutation,
  useUpdateMarketplaceProductMutation,
  useDeleteMarketplaceProductMutation,
  useUploadProductImageMutation,
  useDeleteProductImageMutation,
  useGetMarketplaceOrdersQuery,
  useGetMarketplaceOrderQuery,
  useUpdateMarketplaceOrderStatusMutation,
  useGetMarketplacePaymentsQuery,
  useGetMarketplaceOrderStatsQuery,
  useGetMarketplacePaymentStatsQuery,
  useUpdateMarketplacePaymentStatusMutation,
} = marketplaceApi;
